import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import ChirpPropShapes from 'javascripts/shared/chirp_prop_shapes';

import { BookCard } from 'javascripts/components/book_card';
import { BookCardPlaceholder } from 'javascripts/components/placeholders/book_card_placeholder';
import { FadeIn } from 'javascripts/components/fade_in';

import { bubEvents } from 'javascripts/shared/events/bub_events';

import classNames from 'classnames';
import styles from 'stylesheets/components/book_card_collection_condensed.module';

const CARDS_PER_ROW = {
  default: styles.default,
  3: styles.three,
  4: styles.four,
  5: styles.default
};

const LAST_ROW_CARD_COUNT = {
  default: styles.default,
  1: styles.singleOrphan,
  2: styles.twoOrphans,
  3: styles.threeOrphans,
  4: styles.fourOrphans,
};

export function BookCardCollectionCondensed(props) {
  const lastRowStyle = useMemo(() => {
    if (props.loading) { return 'default'; }
    const remainingBooks = props.audiobooks.length % props.cardsPerRow;
    return remainingBooks <= 4 ? remainingBooks : 'default';
  }, [props.loading, props.audiobooks?.length, props.cardsPerRow]);

  function renderAudiobookCards() {
    return props.audiobooks.map((audiobook, index) => (
      <div key={audiobook.id} role='listitem' className={classNames(styles.item, LAST_ROW_CARD_COUNT[lastRowStyle])}>
        <BookCard
          audiobook={audiobook}
          trackingCategory={props.trackingCategory}
          trackingProperties={{ grid_position: bubEvents.trackingIndex(index) }}
          showCoverButtons={props.showCoverButtons}
          showStarRating={props.showStarRatings}
          showPricing={props.showPricing}
          useBookDetailsModal={props.useBookDetailsModal}
          hideBookDetailsModalButtons={props.hideBookDetailsModalButtons}
          addToCartOnClickHandler={props.addToCartOnClickHandler}
        />
        {props.addBelowCardRender(audiobook)}
      </div>
    ));
  }

  if (props.loading) {
    return (
      <div className={classNames(styles.bookCardCollectionCondensedWide, styles.grid, CARDS_PER_ROW[(props.cardsPerRow || 'default')])}>
        {_.times(props.placeholderCount, i => (
          <BookCardPlaceholder key={i} />
        ))}
      </div>
    );
  }

  const Container = props.animate ? FadeIn : 'div';

  return (
    <Container
      data-testid='book-card-collection-condensed'
      className={classNames(styles.bookCardCollectionCondensedWide, styles.grid, CARDS_PER_ROW[props.cardsPerRow], { [styles.minHeight]: _.size(props.audiobooks) > 5 && props.applyMinHeight, [styles.centerLastRow]: props.centerLastRow })}
      role='list'
    >
      {renderAudiobookCards()}
    </Container>
  );
}

BookCardCollectionCondensed.propTypes = {
  applyMinHeight: PropTypes.bool, // temporary - planning to rip out this and the minHeight style
  animate: PropTypes.bool,
  audiobooks: PropTypes.arrayOf(ChirpPropShapes.audiobook),
  loading: PropTypes.bool,
  showCoverButtons: PropTypes.bool,
  showStarRatings: PropTypes.bool,
  addBelowCardRender: PropTypes.func,
  trackingCategory: PropTypes.string.isRequired,
  cardsPerRow: PropTypes.number,
  centerLastRow: PropTypes.bool,
  placeholderCount: PropTypes.number,
  showPricing: PropTypes.bool,
  useBookDetailsModal: PropTypes.bool,
  hideBookDetailsModalButtons: PropTypes.bool,
  addToCartOnClickHandler: PropTypes.func.isRequired,
};

BookCardCollectionCondensed.defaultProps = {
  applyMinHeight: true,
  animate: false,
  loading: false,
  showCoverButtons: false,
  showStarRatings: false,
  cardsPerRow: 5,
  addBelowCardRender: _.noop,
  centerLastRow: false,
  placeholderCount: 10,
  showPricing: true,
  useBookDetailsModal: false,
  hideBookDetailsModalButtons: false,
  addToCartOnClickHandler: _.noop,
};
